import React, { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Data for Add/Edit modal
  const [deleteId, setDeleteId] = useState(null); // ID of the product to delete
  const [editMode, setEditMode] = useState(false); // Edit mode toggle
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch all products
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/products`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Save or update a product
  const handleSave = async (product) => {
    const method = editMode ? 'PUT' : 'POST';
    const url = editMode
      ? `${apiUrl}/api/products/${product.id}`
      : `${apiUrl}/api/products`;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(product),
      });
      if (!response.ok) throw new Error('Error saving product');
      await fetchProducts();
      setModalData(null); // Close modal
    } catch (err) {
      setError('Error saving product. Please try again.');
    }
  };

  // Delete a product
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/products/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error deleting product');
      await fetchProducts();
      setDeleteId(null); // Close delete confirmation modal
    } catch (err) {
      setError('Error deleting product. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Products</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: '' }); // Open modal with empty data
          }}
        >
          Add Product
        </button>
      </div>

      {loading ? (
        <p>Loading products...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : products.length === 0 ? (
        <p>No products available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(product); // Open modal with product data
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(product.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? 'Edit Product' : 'Add Product'}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name || ''}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)} // Close modal
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>Are you sure you want to delete this product? This action cannot be undone.</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)} // Close modal
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default ProductsTab;
